import { FunctionsHttpError } from '@supabase/supabase-js';

import { getSupabaseClient } from './client';

export class FunctionCallError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly code: string | null,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'FunctionCallError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Invokes an Edge Function with the current user's session. Non-2xx responses
 * become FunctionCallError carrying the server's normalized message.
 */
export async function invokeFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const result = await getSupabaseClient().functions.invoke<T>(name, { body });
  const error: unknown = result.error;
  if (!error) return result.data as T;

  if (error instanceof FunctionsHttpError) {
    const response = error.context as Response;
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
    const serverError = isRecord(payload) && isRecord(payload.error) ? payload.error : null;
    const message =
      typeof serverError?.message === 'string'
        ? serverError.message
        : 'El servidor no pudo completar la operación.';
    const code = typeof serverError?.code === 'string' ? serverError.code : null;
    throw new FunctionCallError(message, response.status, code, payload);
  }
  throw new FunctionCallError(
    'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.',
    null,
    null,
    null,
  );
}
